import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { ITEM_STATUS, OVERALL, describeExpiry } from "./status";

const OVERALL_ORDER = { non_compliant: 0, warning: 1, compliant: 2 };

export default function ClientCompliance() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);
  const [pq, setPq] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("overall");

  useEffect(() => {
    api.get("/config/clients").then((r) => setClients((r.data || []).filter((c) => c.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!clientId) { setData(null); return; }
    api.get(`/training/client-compliance?client_id=${clientId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  let people = data ? [...data.people] : [];
  people = people.filter((p) => !pq || p.name.toLowerCase().includes(pq.toLowerCase()));
  if (stateFilter !== "all") people = people.filter((p) => p.overall === stateFilter);
  const soonest = (p) => {
    const ds = Object.values(p.cells).map((c) => c.days_remaining).filter((d) => d != null && d >= 0);
    return ds.length ? Math.min(...ds) : Infinity;
  };
  if (sortBy === "overall") people.sort((a, b) => OVERALL_ORDER[a.overall] - OVERALL_ORDER[b.overall] || a.name.localeCompare(b.name));
  else if (sortBy === "soonest") people.sort((a, b) => soonest(a) - soonest(b));
  else people.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-4" data-testid="client-compliance-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="compliance-client" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Choose a client…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {data && <>
          <Input data-testid="compliance-person" placeholder="Find person…" value={pq} onChange={(e) => setPq(e.target.value)} className="h-10 w-48" />
          <select data-testid="compliance-state" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="all">All</option><option value="non_compliant">Not compliant</option><option value="warning">Attention</option><option value="compliant">Compliant</option>
          </select>
          <select data-testid="compliance-sort" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="overall">Sort: Overall compliance</option><option value="soonest">Sort: Expiring soonest</option><option value="name">Sort: Person name</option>
          </select>
        </>}
      </div>

      {!clientId && <p className="text-slate-400 text-sm">Select a client to view their compliance matrix.</p>}

      {data && data.requirements.length === 0 && <p className="text-slate-400 text-sm">This client has no training requirements set yet.</p>}

      {data && data.requirements.length > 0 && (
        <div className="bg-white border-2 border-slate-300 rounded-lg overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="bg-slate-200">
                <th className="px-3 py-2 text-left sticky left-0 bg-slate-200 z-10 border-r border-slate-300">Person</th>
                <th className="px-3 py-2 text-left">Overall</th>
                {data.requirements.map((r) => <th key={r.requirement_id} className="px-2 py-2 text-[11px] font-bold text-slate-700 border-l border-slate-200 whitespace-nowrap" title={r.label}>{r.label.length > 18 ? r.label.slice(0, 17) + "…" : r.label}{r.level === "preferred" ? " *" : ""}</th>)}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.profile_id} data-testid={`compliance-row-${p.profile_id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap sticky left-0 bg-white z-10 border-r border-slate-200">{p.name}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${OVERALL[p.overall].cls}`}>{OVERALL[p.overall].label}</span></td>
                  {data.requirements.map((r) => {
                    const c = p.cells[r.requirement_id];
                    const s = ITEM_STATUS[c.status] || ITEM_STATUS.missing;
                    return (
                      <td key={r.requirement_id} className="px-1.5 py-1.5 text-center border-l border-slate-100">
                        <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${s.cls}`} title={describeExpiry(c)}>
                          {c.status === "missing" ? "Missing" : c.status === "no_expiry" ? "No Expiry" : c.status === "expired" ? "Expired" : (c.expiry_date ? new Date(c.expiry_date).toLocaleDateString("en-GB") : "—")}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-xs text-slate-400">* preferred requirement — never forces non-compliance</div>
        </div>
      )}
    </div>
  );
}
